// Subscription check constants
const TOKEN_RETRIEVAL_TIMEOUT_MS = 10000; // 10 seconds
const SUBSCRIPTION_REQUEST_TIMEOUT_MS = 8000; // 8 seconds
const RETRY_DELAY_MS = 1000; // 1 second delay between retries
const SUBSCRIPTION_RETRY_DELAY_MS = 2000; // 2 seconds for subscription retries

// Function to get subscription status with timeout handling and retry logic
const checkSubscriptionStatus = async (retryCount = 0) => {
  let authToken = '';
  
  // Get auth token from Clerk if available
  try {
    if (window.Clerk && window.Clerk.session) {
      // Add timeout to token retrieval
      const tokenPromise = window.Clerk.session.getToken();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Token retrieval timeout')), TOKEN_RETRIEVAL_TIMEOUT_MS)
      );
      
      authToken = await Promise.race([tokenPromise, timeoutPromise]);
      
      if (!authToken) {
        throw new Error('No token received from Clerk');
      }
      
      // Basic token validation
      const tokenParts = authToken.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Invalid token format received');
      }
    } else {
      console.warn('Clerk not available or no session');
    }
  } catch (error) {
    console.error('Error getting auth token:', error);
    
    // If we can't get a token and haven't retried, try once more
    if (retryCount === 0) {
      console.log('Retrying token retrieval...');
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
      return checkSubscriptionStatus(1);
    }
  }
  
  // Create headers with or without token
  const headers = {
    'Content-Type': 'application/json',
  };
  
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  
  try {
    // Add timeout with AbortController
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SUBSCRIPTION_REQUEST_TIMEOUT_MS);
    
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/v1/subscription-status`, {
      method: 'GET',
      headers: headers,
      credentials: 'include',
      signal: controller.signal
    });
    
    // Clear timeout if request completes
    clearTimeout(timeoutId);
    
    // Handle 401 specifically
    if (response.status === 401) {
      console.error('401 Unauthorized - token may be expired or invalid');
      
      // If we have a token and haven't retried, try to refresh and retry
      if (authToken && retryCount === 0) {
        console.log('Attempting to refresh token and retry...');
        
        // Force token refresh by clearing any cached tokens
        if (window.Clerk && window.Clerk.session) {
          try {
            // Try to get a fresh token
            await new Promise(resolve => setTimeout(resolve, 1000));
            return checkSubscriptionStatus(1);
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
          }
        }
      }
      
      return { isPro: false, error: 'Authentication failed' };
    }
    
    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      console.error('Failed to fetch subscription status:', response.status);
      return { isPro: false, error: `Status ${response.status}` };
    }
  } catch (error) {
    console.error('Subscription check error:', error);
    
    // If it's a network error and we haven't retried, try once more
    if (error.name !== 'AbortError' && retryCount === 0) {
      console.log('Retrying subscription check...');
      await new Promise(resolve => setTimeout(resolve, SUBSCRIPTION_RETRY_DELAY_MS));
      return checkSubscriptionStatus(1);
    }
    
    return { isPro: false, error: error.message };
  }
};

export default checkSubscriptionStatus;

// Add a more explicit function for checking Pro status
export const checkIsPro = async () => {
  try {
    // Use the new subscription check function
    const subscriptionData = await checkSubscriptionStatus();
    
    // Return true only if isPro is explicitly true
    return subscriptionData.isPro === true;
  } catch (error) {
    console.error('Error checking Pro status:', error);
    return false;
  }
}; 